import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/config/api';
import DocumentCanvas from '@/components/document-editor/document-canvas';
import FieldInputModal from '@/components/document-viewer/field-input-modal';
import type { SignableFieldData } from '@/components/document-viewer/signable-field';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { Loader2, FileText, CheckCircle, XCircle, ShieldCheck, FileSignature } from 'lucide-react';

interface SigningViewProps {
  token: string;
  otpToken: string;
  onSigningComplete: (signerHasAccount: boolean) => void;
}

interface DocumentData {
  id: number;
  title: string;
  status: string;
  notes?: string;
  requestor: { id: number; full_name: string; email: string };
  recipients: any[];
  signing_requests: any[];
}

export default function SigningView({ token, otpToken, onSigningComplete }: SigningViewProps) {
  const { t } = useTranslation(['signinggate', 'documents', 'common']);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [signableFields, setSignableFields] = useState<SignableFieldData[]>([]);
  const [savedSignatureImage, setSavedSignatureImage] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');

  // Per-field modal state
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [activeField, setActiveField] = useState<SignableFieldData | null>(null);

  // Decline state
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [withdrawConsent, setWithdrawConsent] = useState(false);

  // Finish/submit state
  const [consentChecked, setConsentChecked] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track which signature image was captured (the last one used across all signature fields)
  const [capturedSignatureImage, setCapturedSignatureImage] = useState<string | null>(null);

  const authParam = otpToken ? `otp_token=${otpToken}` : '';
  const authHeader = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    const bearerToken = localStorage.getItem('token');
    if (bearerToken) {
      headers['Authorization'] = `Bearer ${bearerToken}`;
    }
    return headers;
  }, []);

  useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const url = `${API_BASE_URL}/sign/${token}/document${authParam ? `?${authParam}` : ''}`;
        const res = await fetch(url, { headers: authHeader() });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Failed to load document');
        }

        const data = await res.json();
        setDocumentData(data.document);

        if (data.signer_signature_image) {
          setSavedSignatureImage(data.signer_signature_image);
        }

        // Build stream URL
        const streamUrl = `${API_BASE_URL}/sign/${token}/stream${authParam ? `?${authParam}` : ''}`;
        const bearerToken = localStorage.getItem('token');
        const finalStreamUrl = bearerToken && !authParam
          ? `${streamUrl}?token=${bearerToken}`
          : streamUrl;
        setDocumentUrl(finalStreamUrl);

        // Find signer's recipient IDs
        const signerUserId = data.signing_request?.signatory_id;
        const signerEmailVal = data.signing_request?.signatory_email;
        const signerNameVal = data.signing_request?.signatory_name || '';
        setSignerName(signerNameVal);
        setSignerEmail(signerEmailVal || '');

        let signerRecipientIds: string[] = [];
        if (data.document?.recipients) {
          signerRecipientIds = data.document.recipients
            .filter((r: any) =>
              (signerUserId && r.user_id === signerUserId) ||
              (!signerUserId && signerEmailVal && r.email === signerEmailVal)
            )
            .map((r: any) => r.id?.toString());
        }

        // Transform fields into SignableFieldData
        const fields: SignableFieldData[] = (data.fields || []).map((field: any) => {
          const isSigner = signerRecipientIds.includes(field.recipient_id?.toString());
          const baseField: SignableFieldData = {
            id: field.id.toString(),
            type: field.field_type,
            label: field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1),
            x: field.x_position,
            y: field.y_position,
            pageNumber: field.page_number,
            width: field.width,
            height: field.height,
            recipientId: field.recipient_id?.toString(),
            isSignerField: isSigner,
            value: null,
          };

          // If field already has signed data (other signers), mark it completed
          if (field.signed_data) {
            baseField.value = field.signed_data.value || field.signed_data.signatory_name || 'Signed';
            baseField.signatureImage = field.signed_data.signature_image_url || null;
            baseField.fullName = field.signed_data.signatory_name || null;
            baseField.isSignerField = false; // Already signed by someone else
          }

          return baseField;
        });

        setSignableFields(fields);
      } catch (err: any) {
        setError(err.message || t('signinggate:error_generic'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [token, otpToken, authParam, authHeader, t]);

  // Compute progress
  const signerFields = useMemo(() => signableFields.filter(f => f.isSignerField), [signableFields]);
  const completedFields = useMemo(() => signerFields.filter(f => f.value || f.signatureImage), [signerFields]);
  const allFieldsCompleted = signerFields.length > 0 && completedFields.length === signerFields.length;

  // Handle field click
  const handleFieldClick = useCallback((field: SignableFieldData) => {
    setActiveField(field);
    setFieldModalOpen(true);
  }, []);

  // Handle field value confirmation
  const handleFieldConfirm = useCallback((fieldId: string, value: string, signatureImage?: string | null) => {
    setSignableFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return {
        ...f,
        value,
        signatureImage: signatureImage || undefined,
        fullName: (f.type === 'signature' || f.type === 'initial') ? value : undefined,
      };
    }));
    if (signatureImage) {
      setCapturedSignatureImage(signatureImage);
    }
  }, []);

  // Submit sign
  const submitSign = async () => {
    setIsSubmitting(true);

    try {
      const signedFields = signerFields.map(f => ({
        document_field_id: f.id,
        value: f.value || f.fullName || null,
      }));

      const res = await fetch(`${API_BASE_URL}/sign/${token}/sign`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          signed_fields: signedFields,
          remarks: remarks || '',
          otp_token: otpToken || undefined,
          consent: true,
          signature_image: capturedSignatureImage || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to sign document');
      }

      const data = await res.json();

      toast({
        title: t('documents:viewer_toast_approved'),
        description: t('signinggate:post_signing_desc'),
        variant: 'success',
      });

      onSigningComplete(data.signer_has_account ?? false);
    } catch (err: any) {
      toast({
        title: t('documents:viewer_toast_approval_failed'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit decline
  const submitDecline = async () => {
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/sign/${token}/decline`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          decline_reason: declineReason || 'No reason provided',
          withdraw_consent: withdrawConsent,
          otp_token: otpToken || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to decline document');
      }

      toast({
        title: t('documents:viewer_toast_declined'),
        description: t('documents:viewer_toast_declined_desc', { reason: declineReason }),
        variant: 'destructive',
      });

      onSigningComplete(false);
    } catch (err: any) {
      toast({
        title: t('documents:viewer_toast_decline_failed'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <SigningHeader title="" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">{t('signinggate:loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <SigningHeader title="" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
        <SigningHeader title={documentData?.title || ''} />

        <div className="flex flex-1 overflow-hidden">
          {/* PDF Canvas with interactive fields */}
          <div className="flex-1 overflow-auto p-4 bg-muted/20 flex flex-col">
            {documentUrl ? (
              <DocumentCanvas
                documentUrl={documentUrl}
                signingMode
                signableFields={signableFields}
                onFieldClick={handleFieldClick}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p>{t('documents:viewer_no_document')}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l bg-background p-4 hidden lg:flex flex-col">
            <div className="mb-4">
              <h3 className="font-semibold text-sm mb-1">{documentData?.title}</h3>
              <p className="text-xs text-muted-foreground">
                {t('signinggate:sent_by')}: {documentData?.requestor?.full_name}
              </p>
              {documentData?.notes && (
                <p className="text-xs text-muted-foreground mt-1">{documentData.notes}</p>
              )}
            </div>

            {/* Signers list */}
            <div className="mb-4">
              <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                {t('documents:viewer_signers')}
              </h4>
              <div className="space-y-2">
                {(documentData?.signing_requests || []).map((sr: any) => (
                  <div key={sr.id} className="flex items-center gap-2 text-sm">
                    {sr.status === 'signed' ? (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    ) : sr.status === 'declined' ? (
                      <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                    ) : (
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className={sr.status === 'signed' ? 'text-green-700' : sr.status === 'declined' ? 'text-red-700' : ''}>
                      {sr.signatory?.name || sr.signatory?.email || 'Unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Field completion progress */}
            {signerFields.length > 0 && !showDecline && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  {t('documents:field_progress_title')}
                </h4>
                <div className="bg-muted/40 rounded-md p-3">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>{t('documents:field_progress_count', { completed: completedFields.length, total: signerFields.length })}</span>
                    {allFieldsCompleted && <CheckCircle className="h-4 w-4 text-green-500" />}
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${signerFields.length > 0 ? (completedFields.length / signerFields.length) * 100 : 0}%` }}
                    />
                  </div>
                  {!allFieldsCompleted && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('documents:field_progress_hint')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Decline form */}
            {showDecline ? (
              <div className="mt-auto space-y-3">
                <Label htmlFor="decline-reason" className="text-sm font-medium">
                  {t('documents:viewer_decline_reason_label')} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="decline-reason"
                  placeholder={t('signinggate:decline_reason_placeholder')}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="resize-none h-24"
                />
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="gate-withdraw"
                    checked={withdrawConsent}
                    onCheckedChange={(c: boolean | "indeterminate") => setWithdrawConsent(c === true)}
                  />
                  <Label htmlFor="gate-withdraw" className="text-xs leading-tight cursor-pointer">
                    {t('signinggate:withdraw_consent_label')}
                  </Label>
                </div>
                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={!declineReason.trim() || isSubmitting}
                  onClick={submitDecline}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                  {t('documents:viewer_decline_document')}
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => setShowDecline(false)}>
                  {t('documents:field_input_cancel')}
                </Button>
              </div>
            ) : (
              <div className="mt-auto space-y-3">
                {/* ESIGN consent + Finish only when all fields completed */}
                {allFieldsCompleted && (
                  <>
                    <div className="mb-2">
                      <Label htmlFor="gate-remarks" className="text-xs font-medium mb-1 block">
                        {t('documents:viewer_remarks_label')}
                      </Label>
                      <Textarea
                        id="gate-remarks"
                        placeholder={t('documents:viewer_remarks_placeholder')}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        className="resize-none h-16 text-sm"
                      />
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                      <div className="flex items-start gap-2 mb-2">
                        <ShieldCheck className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          {t('documents:esign_disclosure')}
                        </p>
                      </div>
                      <div className="flex items-start gap-2 mt-3">
                        <Checkbox
                          id="gate-consent"
                          checked={consentChecked}
                          onCheckedChange={(c: boolean | "indeterminate") => setConsentChecked(c === true)}
                        />
                        <Label htmlFor="gate-consent" className="text-xs font-medium leading-tight cursor-pointer">
                          {t('documents:esign_consent_label')}
                        </Label>
                      </div>
                    </div>
                  </>
                )}

                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  disabled={!allFieldsCompleted || !consentChecked || isSubmitting}
                  onClick={submitSign}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSignature className="h-4 w-4 mr-2" />}
                  {t('documents:finish_and_sign')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowDecline(true)}
                >
                  {t('signinggate:decline_button')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile bottom bar */}
        <div className="lg:hidden border-t p-3 space-y-2">
          {signerFields.length > 0 && (
            <div className="flex items-center justify-between text-xs px-1 mb-1">
              <span>{t('documents:field_progress_count', { completed: completedFields.length, total: signerFields.length })}</span>
              {allFieldsCompleted && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              disabled={!allFieldsCompleted || isSubmitting}
              onClick={() => {
                if (!consentChecked) {
                  toast({ title: t('documents:esign_consent_required'), variant: 'destructive' });
                  return;
                }
                submitSign();
              }}
            >
              <FileSignature className="h-4 w-4 mr-2" />
              {t('documents:finish_and_sign')}
            </Button>
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowDecline(true)}
            >
              {t('signinggate:decline_button')}
            </Button>
          </div>
        </div>

        {/* Field input modal */}
        <FieldInputModal
          open={fieldModalOpen}
          onOpenChange={setFieldModalOpen}
          field={activeField}
          onConfirm={handleFieldConfirm}
          signerName={signerName}
          signerEmail={signerEmail}
          savedSignatureImage={savedSignatureImage}
        />
      </div>
  );
}

function SigningHeader({ title }: { title: string }) {
  return (
    <header className="border-b bg-background px-4 py-3 flex items-center gap-3">
      <div className="flex items-center gap-2">
        <img src="/beaver.svg" alt="VivoSign" className="h-7 w-7" />
        <span className="font-bold text-base">VivoSign</span>
      </div>
      {title && (
        <>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm truncate">{title}</span>
        </>
      )}
    </header>
  );
}
